"use client";

import * as React from "react";

import { AvailableFundsMetricPanelCard } from "@/components/cards/metric-panel/metric-panel-card";
import { PRODUCT_WIDGET_DOT_GRID } from "@/components/product-widgets/mock-frame";
import { cn } from "@/lib/utils";

/** Figma 8444:61091 — desktop bento inset (551×256). */
const DESKTOP_CANVAS = { width: 551, height: 256 } as const;

/** Figma 9762:32620 — mobile bento inset (279×240). */
const MOBILE_CANVAS = { width: 279, height: 240 } as const;

/** Switch layouts below this container width (mobile frame is 279px). */
const COMPACT_MAX_WIDTH = 400;

const CHECK_CIRCLE = "/assets-next/cards/limited-risk/check-circle.svg";
const FULL_CONTROL_LOCK =
  "/assets-next/cards/limited-risk/full-control-lock.svg";

type PanelLayout = {
  canvas: { width: number; height: number };
  card: { left: number; top: number; width: number; height: number };
  check: { left: number; top: number; size: number };
  fullControl: {
    left: number;
    top: number;
    width: number;
    height: number;
    textSize: string;
    iconSize: number;
    paddingX: number;
  };
};

/** Positions copied from Figma metadata — 8444:61091. */
const DESKTOP_LAYOUT: PanelLayout = {
  canvas: DESKTOP_CANVAS,
  card: { left: 184.07, top: 108, width: 333, height: 291 },
  check: { left: 72.07, top: 97, size: 62 },
  fullControl: {
    left: 263.07,
    top: 25,
    width: 175,
    height: 67,
    textSize: "text-[16.5px]",
    iconSize: 34,
    paddingX: 15,
  },
};

/** Positions copied from Figma metadata — 9762:32620. */
const MOBILE_LAYOUT: PanelLayout = {
  canvas: MOBILE_CANVAS,
  card: { left: 28.51, top: 70.08, width: 333, height: 291 },
  // Check is inside the card frame at (170.82, 95) — panel-relative below.
  check: {
    left: 28.51 + 170.82,
    top: 70.08 + 95,
    size: 62,
  },
  fullControl: {
    left: 110,
    top: 20,
    width: 149,
    height: 61,
    textSize: "text-[14px]",
    iconSize: 34,
    paddingX: 15,
  },
};

function FullControlBadge({ layout }: { layout: PanelLayout }) {
  const { fullControl: badge } = layout;

  return (
    <div
      className="absolute z-20 flex items-center gap-[10px] rounded-2xl bg-white shadow-[2px_4px_18px_rgba(0,0,0,0.1)] backdrop-blur-[9px]"
      style={{
        top: badge.top,
        left: badge.left,
        width: badge.width,
        height: badge.height,
        paddingLeft: badge.paddingX,
        paddingRight: badge.paddingX,
      }}
      data-visual-id="limited-risk-full-control-badge"
    >
      <img
        src={FULL_CONTROL_LOCK}
        alt=""
        style={{ width: badge.iconSize, height: badge.iconSize }}
        className="shrink-0"
        aria-hidden
      />
      <p
        className={cn(
          "leading-[1.5] tracking-[-0.02em] whitespace-nowrap text-[#0c0d0d]",
          badge.textSize
        )}
      >
        Full control
      </p>
    </div>
  );
}

/** Figma 8444:61132 — 62px slot; asset bleeds for drop shadow. */
const CHECK_CIRCLE_SHADOW_INSET = "inset-[-53.69%_-62.64%_-65.62%_-56.68%]";

function CheckCircleBadge({ layout }: { layout: PanelLayout }) {
  const { check } = layout;

  return (
    <div
      className="absolute z-20"
      style={{
        top: check.top,
        left: check.left,
        width: check.size,
        height: check.size,
      }}
      data-visual-id="limited-risk-check-badge"
    >
      <div className={`absolute ${CHECK_CIRCLE_SHADOW_INSET}`}>
        <img
          src={CHECK_CIRCLE}
          alt=""
          aria-hidden
          className="block size-full max-w-none"
        />
      </div>
    </div>
  );
}

function AvailableFundsPanelCanvas({ layout }: { layout: PanelLayout }) {
  const { card } = layout;

  return (
    <div
      className="relative size-full"
      data-visual-id="limited-risk-available-funds-panel"
    >
      <FullControlBadge layout={layout} />
      <CheckCircleBadge layout={layout} />

      <AvailableFundsMetricPanelCard
        className="absolute z-10"
        style={{
          top: card.top,
          left: card.left,
          width: card.width,
          height: card.height,
        }}
      />
    </div>
  );
}

export interface AvailableFundsLimitedRiskPanelProps {
  className?: string;
  compact?: boolean;
  "data-testid"?: string;
}

/** DTC limited-risk inset — dot grid with Full control, check, and funds card. */
export function AvailableFundsLimitedRiskPanel({
  className,
  compact = false,
  "data-testid": dataTestId = "limited-risk-available-funds-panel",
}: AvailableFundsLimitedRiskPanelProps) {
  const layout = compact ? MOBILE_LAYOUT : DESKTOP_LAYOUT;

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        "border-grey-100 bg-grey-0 relative overflow-hidden rounded-lg border lg:rounded-none lg:border-0",
        PRODUCT_WIDGET_DOT_GRID,
        className
      )}
      style={{
        width: layout.canvas.width,
        height: layout.canvas.height,
      }}
    >
      <AvailableFundsPanelCanvas layout={layout} />
    </div>
  );
}

function usePanelLayout(containerWidth: number) {
  return containerWidth < COMPACT_MAX_WIDTH ? MOBILE_LAYOUT : DESKTOP_LAYOUT;
}

/** Scales the panel to fit fluid containers (e.g. DTC bento row). */
export function AvailableFundsLimitedRiskPanelScaled({
  className,
}: {
  className?: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);
  const [layout, setLayout] = React.useState(DESKTOP_LAYOUT);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateScale = () => {
      const nextLayout = usePanelLayout(node.clientWidth);
      setLayout(nextLayout);

      setScale(
        Math.min(
          1,
          node.clientWidth / nextLayout.canvas.width,
          node.clientHeight / nextLayout.canvas.height
        )
      );
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full overflow-hidden", className)}
      style={{ height: layout.canvas.height * scale }}
    >
      <div
        className={cn(
          "origin-top-left",
          PRODUCT_WIDGET_DOT_GRID,
          "border-grey-100 bg-grey-0 absolute top-0 left-0 overflow-hidden rounded-lg border lg:rounded-none lg:border-0"
        )}
        style={{
          width: layout.canvas.width,
          height: layout.canvas.height,
          transform: `scale(${scale})`,
        }}
      >
        <AvailableFundsPanelCanvas layout={layout} />
      </div>
    </div>
  );
}

export { DESKTOP_CANVAS, DESKTOP_LAYOUT, MOBILE_CANVAS, MOBILE_LAYOUT };
